import { Link } from 'react-router-dom'
import { useOptionalT } from '../i18n/LocaleProvider'
import type { FormEvent } from 'react'

export interface SidebarCategory {
  label: string
  /** Rendered inside a `<span>`, e.g. `(08)`. */
  count: string
  to: string
}

export interface SidebarPost {
  image: string
  title: string
  date: string
  to: string
}

export interface SidebarTag {
  label: string
  to: string
}

export interface InnerSidebarProps {
  searchTitle?: string
  searchPlaceholder?: string
  categoriesTitle?: string
  categories: SidebarCategory[]
  postsTitle?: string
  posts: SidebarPost[]
  tagsTitle?: string
  tags: SidebarTag[]
  onSearch?: (query: string) => void
}

/**
 * The `.ul-inner-sidebar` column: search, categories, recent posts, tag cloud.
 *
 * Identical across all four pages that use it — Event Details, Donation Details,
 * Blog Details and the alternate Blog layout — so it takes content props but no
 * per-page structural configuration.
 */
export function InnerSidebar({
  searchTitle: searchTitleProp,
  searchPlaceholder: searchPlaceholderProp,
  categoriesTitle: categoriesTitleProp,
  categories,
  postsTitle: postsTitleProp,
  posts,
  tagsTitle: tagsTitleProp,
  tags,
  onSearch,
}: InnerSidebarProps) {
  const t = useOptionalT()
  const searchTitle = searchTitleProp ?? t('ul.sidebar.search')
  const searchPlaceholder = searchPlaceholderProp ?? t('ul.sidebar.searchPlaceholder')
  const categoriesTitle = categoriesTitleProp ?? t('ul.sidebar.categories')
  const postsTitle = postsTitleProp ?? t('ul.sidebar.posts')
  const tagsTitle = tagsTitleProp ?? t('ul.sidebar.tags')
  const handleSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const value = new FormData(e.currentTarget).get('blog-search')
    onSearch?.(typeof value === 'string' ? value : '')
  }

  return (
    <div className="ul-inner-sidebar">
      {/* single widget / search */}
      <div className="ul-inner-sidebar-widget ul-inner-sidebar-search">
        <h3 className="ul-inner-sidebar-widget-title">{searchTitle}</h3>
        <div className="ul-inner-sidebar-widget-content">
          <form action="#" className="ul-blog-search-form" onSubmit={handleSearch}>
            <input type="search" name="blog-search" id="ul-blog-search" placeholder={searchPlaceholder} />
            <button type="submit" aria-label="Search">
              <span className="icon">
                <i className="flaticon-search" />
              </span>
            </button>
          </form>
        </div>
      </div>

      {/* single widget / Categories */}
      <div className="ul-inner-sidebar-widget categories">
        <h3 className="ul-inner-sidebar-widget-title">{categoriesTitle}</h3>
        <div className="ul-inner-sidebar-widget-content">
          <div className="ul-inner-sidebar-categories">
            {categories.map((cat) => (
              <Link key={cat.label} to={cat.to}>
                {cat.label} <span>{cat.count}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* single widget / Recent Posts */}
      <div className="ul-inner-sidebar-widget posts">
        <h3 className="ul-inner-sidebar-widget-title">{postsTitle}</h3>
        <div className="ul-inner-sidebar-widget-content">
          <div className="ul-inner-sidebar-posts">
            {posts.map((post, i) => (
              <div className="ul-inner-sidebar-post" key={`${post.image}-${i}`}>
                <div className="img">
                  <img src={post.image} alt="Post Image" />
                </div>
                <div className="txt">
                  <h4 className="title">
                    <Link to={post.to}>{post.title}</Link>
                  </h4>
                  {/* leading space inside `.date` is intentional spacing before the span */}
                  <span className="date">
                    {' '}
                    <span>{post.date}</span>
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* single widget / Tags */}
      <div className="ul-inner-sidebar-widget tags">
        <h3 className="ul-inner-sidebar-widget-title">{tagsTitle}</h3>
        <div className="ul-inner-sidebar-widget-content">
          <div className="ul-inner-sidebar-tags">
            {tags.map((tag) => (
              <Link key={tag.label} to={tag.to}>
                {tag.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
